import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

const SEOHead = () => {
  const { language, currency, getPrice, formatPrice } = useLanguage();
  const price = getPrice('launch');

  useEffect(() => {
    // Structured Data - Local Business
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://www.fuel72.com",
      "name": "Fuel72",
      "description": language === "de" 
        ? "Professionelle UGC-Video-Erstellung für lokale Unternehmen. Ihr Content-Partner für Sichtbarkeit. 72 Stunden Lieferung."
        : "Professional UGC video creation for local businesses. Your content supply partner for visibility. 72-hour delivery.",
      "url": "https://www.fuel72.com",
      "telephone": "+4917687870011",
      "email": "hello@fuel72.com",
      "priceRange": currency + price,
      "image": "https://www.fuel72.com/og-image.jpg",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": language === "de" ? "DE" : "US",
        "addressRegion": language === "de" ? "Rheinland-Pfalz" : "California"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": language === "de" ? "50.4334" : "37.7749",
        "longitude": language === "de" ? "7.5881" : "-122.4194"
      },
      "areaServed": language === "de" 
        ? ["DE", "AT", "CH"]
        : ["US", "CA", "UK", "AU"],
      "openingHours": "Mo-Fr 09:00-18:00",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "200",
        "bestRating": "5",
        "worstRating": "1"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Content Creation Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": language === "de" ? "72-Stunden Content-Paket" : "72-Hour Content Package",
              "description": language === "de"
                ? "6 professionelle UGC-Videos + 12 Poster + Bildunterschriften"
                : "6 professional UGC videos + 12 posters + captions",
              "serviceType": "Content Creation"
            },
            "price": price,
            "priceCurrency": language === "de" ? "EUR" : "USD",
            "availability": "https://schema.org/InStock",
            "priceValidUntil": "2025-12-31"
          }
        ]
      }
    };

    // Structured Data - Service
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Video Content Creation",
      "provider": {
        "@type": "Organization",
        "name": "Fuel72",
        "url": "https://www.fuel72.com"
      },
      "areaServed": language === "de" ? "Germany" : "United States",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Content Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": language === "de" ? "UGC Video-Produktion" : "UGC Video Production",
              "description": language === "de"
                ? "Authentische Video-Inhalte für soziale Medien und Online-Marketing"
                : "Authentic video content for social media and online marketing"
            }
          }
        ]
      }
    };

    // Structured Data - FAQ
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": language === "de" ? "Wie viel kostet das?" : "How much does this cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === "de"
              ? `${currency}${price} pauschal für alles. 6 Videos, 12 Poster, Bildunterschriften, 72-Stunden-Lieferung. Keine Einrichtungsgebühren, keine monatlichen Abonnements, keine versteckten Kosten.`
              : `${currency}${price} flat for everything. 6 videos, 12 posters, captions, 72-hour delivery. No setup fees, no monthly subscriptions, no hidden costs.`
          }
        },
        {
          "@type": "Question",
          "name": language === "de" ? "Was ist, wenn es mir nicht gefällt?" : "What if I don't like it?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === "de"
              ? "Wir bieten eine kostenlose Überarbeitungsrunde an. Wenn etwas nicht stimmt, beheben wir es. Wir sind erst fertig, wenn Sie zufrieden sind."
              : "We include one round of free revisions. If something isn't right, we fix it. We're not done until you're happy."
          }
        },
        {
          "@type": "Question",
          "name": language === "de" ? "Wie verwende ich die Videos und Poster?" : "How do I use the videos and posters?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === "de"
              ? "Jede Datei ist voroptimiert für: Google Business Profile, Instagram Reels & Feed, Facebook, TikTok, WhatsApp Business, Twitter/LinkedIn. Einfach herunterladen und hochladen."
              : "Every file comes pre-optimized for: Google Business Profile, Instagram Reels & Feed, Facebook, TikTok, WhatsApp Business, Twitter/LinkedIn. Just download and upload."
          }
        }
      ]
    };

    // Structured Data - HowTo
    const howToSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": language === "de" 
        ? "Wie Sie in 72 Stunden professionelle Inhalte für Ihr lokales Geschäft erhalten"
        : "How to Get Professional Content for Your Local Business in 72 Hours",
      "description": language === "de"
        ? "Einfacher 4-Schritte-Prozess, um professionelle Videos und Poster für Ihr Unternehmen zu erhalten"
        : "Simple 4-step process to get professional videos and posters for your business",
      "totalTime": "PT72H",
      "step": [
        {
          "@type": "HowToStep",
          "name": language === "de" ? "10 Fotos senden" : "Send 10 Photos",
          "text": language === "de"
            ? "Senden Sie uns per WhatsApp 10 Fotos Ihres Geschäfts + Ihr Logo. Dauert 60 Sekunden."
            : "WhatsApp us 10 photos of your business + your logo. Takes 60 seconds.",
          "position": 1
        },
        {
          "@type": "HowToStep",
          "name": language === "de" ? "Kurzes Briefing" : "Quick Brief",
          "text": language === "de"
            ? "Sagen Sie uns in einer kurzen WhatsApp-Nachricht, was Sie besonders macht."
            : "Tell us what makes you special in a quick WhatsApp message.",
          "position": 2
        },
        {
          "@type": "HowToStep",
          "name": language === "de" ? "Wir erstellen" : "We Create",
          "text": language === "de"
            ? "Unser Team produziert 6 professionelle Videos + 12 Poster mit Texten."
            : "Our team produces 6 professional videos + 12 posters with captions.",
          "position": 3
        },
        {
          "@type": "HowToStep",
          "name": language === "de" ? "Download & Posten" : "Download & Post",
          "text": language === "de"
            ? "Erhalten Sie alles per WhatsApp-Link. Bereit zum direkten Hochladen."
            : "Receive everything via WhatsApp link. Ready to upload directly.",
          "position": 4
        }
      ]
    };

    // Organization Schema - Enhanced for AI
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Fuel72",
      "url": "https://www.fuel72.com",
      "logo": "https://www.fuel72.com/logo.png",
      "sameAs": [
        "https://www.instagram.com/fuel72",
        "https://www.facebook.com/fuel72"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+4917687870011",
        "contactType": "Customer Service",
        "availableLanguage": ["German", "English"],
        "areaServed": language === "de" ? ["DE", "AT", "CH"] : ["US", "CA", "UK", "AU"]
      },
      "knowsAbout": [
        "UGC Video Production",
        "Social Media Marketing",
        "Local Business Marketing",
        "Content Creation",
        "Instagram Reels",
        "TikTok Content",
        "Google Business Profile Optimization"
      ]
    };

    // WebSite Schema with SearchAction
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Fuel72",
      "url": "https://www.fuel72.com",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.fuel72.com/?s={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };

    // Product Schema
    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": language === "de" ? "72-Stunden Content-Paket" : "72-Hour Content Package",
      "description": language === "de"
        ? "6 professionelle UGC-Videos + 12 Poster + Bildunterschriften für lokale Unternehmen. In 72 Stunden geliefert."
        : "6 professional UGC videos + 12 posters + captions for local businesses. Delivered in 72 hours.",
      "brand": {
        "@type": "Brand",
        "name": "Fuel72"
      },
      "offers": {
        "@type": "Offer",
        "price": price,
        "priceCurrency": language === "de" ? "EUR" : "USD",
        "availability": "https://schema.org/InStock",
        "priceValidUntil": "2025-12-31",
        "url": "https://www.fuel72.com"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "200",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // Insert all schemas
    const schemas = [
      localBusinessSchema,
      serviceSchema,
      faqSchema,
      howToSchema,
      organizationSchema,
      websiteSchema,
      productSchema
    ];

    // Remove existing schema scripts
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    existingSchemas.forEach(script => script.remove());

    // Add all schemas
    schemas.forEach((schema, index) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = `schema-${index}`;
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // Update meta tags based on language
    const metaTitle = language === "de"
      ? "Fuel72 | Professionelle UGC-Videos in 72 Stunden | Content = Treibstoff"
      : "Fuel72 | Professional UGC Videos in 72 Hours | Content = Fuel";

    const metaDescription = language === "de"
      ? "Ihr Content-Partner für Sichtbarkeit. Professionelle UGC-Videos in 72 Stunden. Kein Filmen, kein Lernen. Content = Treibstoff für Ihr Business."
      : "Your content supply partner for visibility. Professional UGC videos in 72 hours. No filming, no learning curve. Content = Fuel for your business.";

    const metaKeywords = language === "de"
      ? "ugc videos, content treibstoff, video content, social media marketing, lokales geschäft marketing, instagram reels, tiktok videos, 72 stunden lieferung, content partner, restaurant marketing, salon marketing, fitness studio marketing"
      : "ugc videos, content fuel, video content, social media marketing, local business marketing, instagram reels, tiktok content, 72 hour delivery, content supply partner, restaurant marketing, salon marketing, gym marketing";

    document.title = metaTitle;
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    updateMetaTag("description", metaDescription);
    updateMetaTag("keywords", metaKeywords);
    updateMetaTag("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    updateMetaTag("geo.region", language === "de" ? "DE-RP" : "US-CA");
    updateMetaTag("geo.placename", language === "de" ? "Koblenz" : "San Francisco");
    updateMetaTag("geo.position", language === "de" ? "50.4334;7.5881" : "37.7749;-122.4194");
    updateMetaTag("ICBM", language === "de" ? "50.4334, 7.5881" : "37.7749, -122.4194");
    
    // Open Graph
    updateMetaTag("og:title", metaTitle, true);
    updateMetaTag("og:description", metaDescription, true);
    updateMetaTag("og:type", "website", true);
    updateMetaTag("og:url", "https://www.fuel72.com", true);
    updateMetaTag("og:image", "https://www.fuel72.com/og-image.jpg", true);
    updateMetaTag("og:locale", language === "de" ? "de_DE" : "en_US", true);
    updateMetaTag("og:site_name", "Fuel72", true);
    
    // Twitter Card
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", metaTitle);
    updateMetaTag("twitter:description", metaDescription);
    updateMetaTag("twitter:image", "https://www.fuel72.com/og-image.jpg");
    
    // AI Search Optimization
    updateMetaTag("author", "Fuel72");
    updateMetaTag("coverage", "Worldwide");
    updateMetaTag("distribution", "Global");
    updateMetaTag("rating", "General");
    updateMetaTag("revisit-after", "7 days");

    // Clean up on unmount
    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-${index}`);
        if (script) script.remove();
      });
    };
  }, [language, currency, price]);

  return null;
};

export default SEOHead;
